// SSE chat stream — replaces the previous socket.io transport. The backend
// emits `text/event-stream` over POST /chat?stream=true; we use fetch +
// ReadableStream because the EventSource API only supports GET.
//
// The async iterator surface lets pages consume events with `for await` and
// keeps cancellation simple via AbortController.

import { API_BASE, readCachedAccessToken } from "../api/client";
import type {
  AnswerEventData,
  StatusEventData,
  StreamEvent,
  StreamEventKind,
  ToolCallEventData,
  ToolResultEventData,
} from "@/types/eryndal";

export interface AskOptions {
  query: string;
  sessionId?: string | null;
  attachments?: string[];
  forceClass?: string | null;
  signal?: AbortSignal;
  getAccessToken?: () => Promise<string | null>;
}

export interface ParsedSSE {
  event: string;
  data: string;
}

export type ChatStreamEvent =
  | (StreamEvent<StatusEventData> & { kind: "status" })
  | (StreamEvent<ToolCallEventData> & { kind: "tool_call" })
  | (StreamEvent<ToolResultEventData> & { kind: "tool_result" })
  | (StreamEvent<AnswerEventData> & { kind: "answer" })
  | (StreamEvent<unknown> & { kind: Exclude<StreamEventKind, "status" | "tool_call" | "tool_result" | "answer"> });

function* parseSseChunks(buffer: string): Generator<ParsedSSE, string> {
  const events: ParsedSSE[] = [];
  let cursor = 0;
  while (true) {
    const boundary = buffer.indexOf("\n\n", cursor);
    if (boundary === -1) break;
    const raw = buffer.slice(cursor, boundary);
    cursor = boundary + 2;
    let event = "message";
    const dataLines: string[] = [];
    for (const line of raw.split("\n")) {
      if (line.startsWith("event: ")) event = line.slice(7).trim();
      else if (line.startsWith("data: ")) dataLines.push(line.slice(6));
    }
    events.push({ event, data: dataLines.join("\n") });
  }
  for (const ev of events) yield ev;
  return buffer.slice(cursor);
}

export async function* askChatStream(opts: AskOptions): AsyncGenerator<ChatStreamEvent> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  let token: string | null = null;
  if (opts.getAccessToken) {
    try {
      token = await opts.getAccessToken();
    } catch {
      token = null;
    }
  }
  if (!token) token = readCachedAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}/chat?stream=true`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      query: opts.query,
      session_id: opts.sessionId ?? undefined,
      attachments: opts.attachments ?? [],
      ...(opts.forceClass ? { force_class: opts.forceClass } : {}),
    }),
    signal: opts.signal,
  });

  if (!response.ok || !response.body) {
    throw new Error(`Chat stream request failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Drain any complete events out of the buffer.
      let nextBoundary = buffer.indexOf("\n\n");
      while (nextBoundary !== -1) {
        const raw = buffer.slice(0, nextBoundary);
        buffer = buffer.slice(nextBoundary + 2);
        const parsed = parseEventBlock(raw);
        if (parsed) yield parsed;
        nextBoundary = buffer.indexOf("\n\n");
      }
    }
    if (buffer.trim().length) {
      const parsed = parseEventBlock(buffer);
      if (parsed) yield parsed;
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // releaseLock can throw if the stream is already locked elsewhere; ignore.
    }
  }
}

function parseEventBlock(raw: string): ChatStreamEvent | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line.startsWith("event: ")) event = line.slice(7).trim();
    else if (line.startsWith("data: ")) dataLines.push(line.slice(6));
  }
  if (!event || event === "message") return null;
  let payload: unknown = dataLines.join("\n");
  try {
    payload = JSON.parse(dataLines.join("\n"));
  } catch {
    // Some events are pings without payload; pass through as-is.
  }
  return { kind: event as StreamEventKind, data: payload } as ChatStreamEvent;
}

export { parseSseChunks };
